import { useCallback, useEffect, useRef, useState } from 'react'
import { useApolloClient } from '@apollo/client'

import participantsQuery from '../graphql/query/participants'

// The `limit` arg of the generated `participants` query defaults to 1000, so a
// query that omits it silently truncates large events. Page through the results
// explicitly and merge them so the full list is always returned.
const BATCH_SIZE = 5000
// Safety bound so a server that ignores `skip` can never spin forever.
const MAX_BATCHES = 100

export const useParticipants = (eventId, { pollInterval = 0 } = {}) => {
  const client = useApolloClient()
  const [participants, setParticipants] = useState([])
  const [loading, setLoading] = useState(Boolean(eventId))
  const [error, setError] = useState(null)

  // Monotonic request generation; batches arriving after a newer request
  // (event switch / refetch) are discarded.
  const requestIdRef = useRef(0)
  // Request id of the currently in-flight fetch, or null.
  const activeFetchIdRef = useRef(null)

  const fetchAll = useCallback(
    async ({ silent = false, force = false } = {}) => {
      if (!eventId) {
        return { data: { participants: [] } }
      }
      // A fetch is already running and this is not a forced start (event
      // switch / user-initiated refresh), so skip it — e.g. a poll firing
      // while a manual refetch is running.
      if (activeFetchIdRef.current !== null && !force) {
        return { data: undefined }
      }

      const requestId = ++requestIdRef.current
      activeFetchIdRef.current = requestId
      if (!silent) setLoading(true)
      setError(null)
      const isCurrent = () => requestIdRef.current === requestId

      try {
        const merged = []
        let skip = 0
        let complete = false
        for (let batchIndex = 0; batchIndex < MAX_BATCHES; batchIndex += 1) {
          // eslint-disable-next-line no-await-in-loop
          const { data } = await client.query({
            query: participantsQuery,
            variables: {
              filter: { eventId },
              skip,
              limit: BATCH_SIZE,
              sort: '_ID_ASC',
            },
            // `no-cache` keeps tens of thousands of participants out of the
            // Apollo cache: nothing reads the `participants` root field from
            // there, and the merged list already lives in this hook's state.
            fetchPolicy: 'no-cache',
          })

          // A newer request (event switch / refetch) superseded this one.
          if (!isCurrent()) {
            return { data: undefined }
          }

          const batch = data?.participants || []
          merged.push(...batch)
          // Stop only on an empty batch. A short batch can also mean the server
          // clamped `limit`, and breaking there would silently truncate the
          // list — the exact bug this hook exists to avoid.
          if (batch.length === 0) {
            complete = true
            break
          }
          skip += batch.length
        }

        if (!complete) {
          console.warn(
            `useParticipants: stopped after ${MAX_BATCHES} batches (${merged.length} records); the list may be incomplete.`
          )
        }

        if (isCurrent()) {
          setParticipants(merged)
        }
        return { data: { participants: merged } }
      } catch (err) {
        // Keep the previous list rendered; surface the failure via `error`.
        if (isCurrent()) {
          setError(err)
        }
        throw err
      } finally {
        if (activeFetchIdRef.current === requestId) {
          activeFetchIdRef.current = null
        }
        if (isCurrent() && !silent) {
          setLoading(false)
        }
      }
    },
    [client, eventId]
  )

  const refetch = useCallback(
    async (options) => {
      try {
        return await fetchAll(options)
      } catch {
        return { data: undefined }
      }
    },
    [fetchAll]
  )

  // Initial fetch, and refetch when the event changes.
  useEffect(() => {
    requestIdRef.current += 1 // invalidate in-flight batches from the previous event
    setParticipants([])
    setError(null)
    if (!eventId) {
      setLoading(false)
      return undefined
    }
    refetch({ force: true })
    // Bumping the generation stops the remaining batches of an in-flight chain
    // on unmount, instead of paging through the whole event for nothing.
    return () => {
      requestIdRef.current += 1
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refetch])

  // Optional polling (StockPage refreshes every 20s). Silent so polling never
  // flips `loading` and flashes the page.
  useEffect(() => {
    if (!eventId || !pollInterval) return undefined
    const intervalId = setInterval(() => {
      refetch({ silent: true })
    }, pollInterval)
    return () => clearInterval(intervalId)
  }, [eventId, pollInterval, refetch])

  return { participants, loading, error, refetch }
}

export default useParticipants
